/**
 * Cookie / tracker consent: the single source of truth.
 *
 * Under the ePrivacy Directive (art. 5(3)) and the GDPR, anything that is not strictly
 * necessary to deliver the service the visitor asked for may only run after they opt
 * in. That is GTM (analytics + advertising tags); the cart, session and login entries
 * in localStorage, and the Tawk.to support chat, are treated as necessary and are not
 * covered.
 *
 * The decision lives in localStorage rather than a cookie: it is read only by this
 * site's own scripts, so nothing needs to be sent to the server on every request.
 * It expires after 12 months and when CONSENT_VERSION changes (for example when a new
 * service is added), so the visitor is asked again only when it is meaningful.
 */

import { useSyncExternalStore } from 'react';

export const CONSENT_STORAGE_KEY = 'cookie-consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export interface ConsentChoices {
  /** Google Tag Manager: analytics and advertising measurement. */
  analytics: boolean;
}

export interface ConsentSnapshot extends ConsentChoices {
  /** False on the server and before the first client read. */
  ready: boolean;
  /** A valid, unexpired decision exists. */
  decided: boolean;
  /** The banner should be on screen. */
  bannerOpen: boolean;
}

interface StoredConsent {
  v: number;
  a: boolean;
  t: number;
}

const SERVER_SNAPSHOT: ConsentSnapshot = {
  ready: false,
  decided: false,
  analytics: false,
  bannerOpen: false,
};

const listeners = new Set<() => void>();

/** Used when localStorage throws (private mode, blocked site data). */
let memoryRaw: string | null = null;
let reopenRequested = false;
let cached: { key: string; snapshot: ConsentSnapshot } | null = null;

const readRaw = (): string | null => {
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch (error) {
    return memoryRaw;
  }
};

const parse = (raw: string | null): StoredConsent | null => {
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as StoredConsent;

    if (
      value?.v !== CONSENT_VERSION ||
      typeof value.t !== 'number' ||
      Date.now() - value.t > CONSENT_MAX_AGE_MS
    ) {
      return null;
    }

    return value;
  } catch (error) {
    return null;
  }
};

const emit = () => listeners.forEach(listener => listener());

export const getConsentSnapshot = (): ConsentSnapshot => {
  if (typeof window === 'undefined') return SERVER_SNAPSHOT;

  const raw = readRaw();
  const stored = parse(raw);
  const key = `${stored ? raw : ''}|${reopenRequested}`;

  // useSyncExternalStore needs a referentially stable snapshot between changes.
  if (cached?.key === key) return cached.snapshot;

  const snapshot: ConsentSnapshot = {
    ready: true,
    decided: Boolean(stored),
    analytics: stored?.a ?? false,
    bannerOpen: !stored || reopenRequested,
  };

  cached = { key, snapshot };

  return snapshot;
};

const onStorage = (event: StorageEvent) => {
  if (event.key === CONSENT_STORAGE_KEY || event.key === null) emit();
};

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
};

/** First-party cookies that GTM/GA tags set; removed when consent is withdrawn. */
const TRACKING_COOKIE = /^(_ga|_gid|_gat|_gcl|_fbp|_gac)/i;

const clearTrackingCookies = () => {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = [
    undefined,
    host,
    ...parts.map((_, i) => `.${parts.slice(i).join('.')}`),
  ];

  document.cookie.split(';').forEach(entry => {
    const name = entry.split('=')[0].trim();

    if (!TRACKING_COOKIE.test(name)) return;

    domains.forEach(domain => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ''
      }`;
    });
  });
};

export const saveConsent = (choices: ConsentChoices): void => {
  if (typeof window === 'undefined') return;

  const previous = getConsentSnapshot();
  const stored: StoredConsent = {
    v: CONSENT_VERSION,
    a: choices.analytics,
    t: Date.now(),
  };
  const raw = JSON.stringify(stored);

  memoryRaw = raw;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, raw);
  } catch (error) {
    // Kept in memory for this page view only.
  }

  reopenRequested = false;
  emit();

  // A script that is already running cannot be unloaded, so withdrawing consent
  // reloads the page without it.
  if (previous.analytics && !choices.analytics) {
    clearTrackingCookies();
    window.location.reload();
  }
};

export const acceptAllConsent = () => saveConsent({ analytics: true });
export const rejectAllConsent = () => saveConsent({ analytics: false });

/** Re-opens the banner (footer / cookie-policy link) so the choice can be changed. */
export const openConsentSettings = (): void => {
  reopenRequested = true;
  emit();
};

export const hasConsent = (category: keyof ConsentChoices): boolean =>
  getConsentSnapshot()[category];

export const useConsent = (): ConsentSnapshot =>
  useSyncExternalStore(subscribe, getConsentSnapshot, () => SERVER_SNAPSHOT);
